import { createSlice, type PayloadAction } from "@reduxjs/toolkit";

import type { PreChangeCheckedInLegs } from "@repo/ui/features/modify-booking/utils/involuntaryChangeCheckInReminder";
import { isInvoluntaryChangeFromQueues } from "@repo/ui/features/modify-booking/utils/involuntaryChangeQueues";
import type { BookingDetails } from "@repo/ui/types/booking";
import type { Journeyv2 } from "@repo/ui/types/modifyBooking";
import type { FlightInfo, Seat } from "@repo/ui/types/seats";
import type { UserInfo } from "@repo/ui/types/userInfo";

import { useAppSelector } from "../hooks";
import type { Passenger as PassengerInfo } from "./passengerSlice";

// Type for passenger field values
type PassengerFieldValue = string | number | boolean | object | null | undefined;

// ---------- Types ----------
export interface ModifiedBookingFlightSegment {
  id: string; // unique segment identifier
  flightInfo: FlightInfo;
  seats?: Seat[];
  passengers: PassengerInfo[];
  arrivalStationCode?: string;
  departureStationCode?: string;
  arrivalDate?: string;
  departureDate?: string;
}

export interface ModifiedBookingEmergencyContact {
  name: string;
  phone: string;
  email: string;
}

export interface ModifiedBookingFlowState {
  outbound: ModifiedBookingFlightSegment[];
  inbound: ModifiedBookingFlightSegment[];
  passengers: PassengerInfo[];
  journeyType: "oneway" | "roundtrip";
  departure: string;
  destination: string;
  travelDates: { from: string; to?: string; fromUTC?: string; toUTC?: string };
  isModifying?: boolean;
  timeVariant?: {
    outbound: number;
    inbound: number;
  };
  passengersCount: number;
  prohibitedItemsDeclared?: boolean;
  isHydrated: boolean; // optional flag to indicate slice has been initialized
  recordLocator?: string;
  departureCode?: string;
  destinationCode?: string;
  inboundSubtotal?: number;
  outboundSubtotal?: number;
}

export interface ModifyBookingState {
  selectedLeg: "outbound" | "return" | "both" | null;

  outboundDate?: string;
  returnDate?: string;

  selectedFlights: {
    outbound: Journeyv2 | null;
    return: Journeyv2 | null;
  };

  selectedFareDifference: {
    outbound: number | null;
    return: number | null;
  };

  // Modified booking state (checkinSlice implementation)
  modifiedBooking: ModifiedBookingFlowState;

  originalBookingDetails: BookingDetails | null;
  preChangeCheckedInLegs: PreChangeCheckedInLegs | null;
  bookingUserInfo: UserInfo | null;
}

export const initialState: ModifyBookingState = {
  selectedLeg: null,
  outboundDate: undefined,
  returnDate: undefined,
  selectedFlights: {
    outbound: null,
    return: null,
  },
  selectedFareDifference: {
    outbound: null,
    return: null,
  },
  // Modified booking state (checkinSlice implementation)
  modifiedBooking: {
    outbound: [],
    inbound: [],
    passengers: [],
    journeyType: "oneway",
    departure: "",
    destination: "",
    travelDates: { from: "" },
    passengersCount: 0,
    isHydrated: false,
    recordLocator: "",
    departureCode: "",
    destinationCode: "",
    isModifying: false,
    inboundSubtotal: 0,
    outboundSubtotal: 0,
  },

  originalBookingDetails: null,
  preChangeCheckedInLegs: null,
  bookingUserInfo: null,
};

const modifyBookingSlice = createSlice({
  name: "modifyBooking",
  initialState,
  reducers: {
    setSelectedLeg: (state, action: PayloadAction<"outbound" | "return" | "both" | null>) => {
      state.selectedLeg = action.payload;
    },

    setOutboundDate: (state, action: PayloadAction<string | undefined>) => {
      state.outboundDate = action.payload;
    },

    setReturnDate: (state, action: PayloadAction<string | undefined>) => {
      state.returnDate = action.payload;
    },

    setSelectedOutboundFlight: (state, action: PayloadAction<Journeyv2 | null>) => {
      state.selectedFlights.outbound = action.payload;
    },

    setSelectedReturnFlight: (state, action: PayloadAction<Journeyv2 | null>) => {
      state.selectedFlights.return = action.payload;
    },

    setSelectedOutboundFareDifference: (state, action: PayloadAction<number | null>) => {
      state.selectedFareDifference.outbound = action.payload;
    },

    setSelectedReturnFareDifference: (state, action: PayloadAction<number | null>) => {
      state.selectedFareDifference.return = action.payload;
    },

    resetModifyBooking: () => initialState,

    // Modified booking actions (checkinSlice implementation)
    setModifiedBooking: (state, action: PayloadAction<Partial<ModifiedBookingFlowState>>) => {
      const { inboundSubtotal, outboundSubtotal } = state.modifiedBooking;
      const payload = action.payload;

      Object.assign(state.modifiedBooking, payload);

      if (payload.inboundSubtotal === undefined) {
        state.modifiedBooking.inboundSubtotal = inboundSubtotal;
      }

      if (payload.outboundSubtotal === undefined) {
        state.modifiedBooking.outboundSubtotal = outboundSubtotal;
      }

      state.modifiedBooking.isHydrated = true;
      state.modifiedBooking.isModifying = true;
    },
    resetModifiedBooking: (state) => {
      state.modifiedBooking = initialState.modifiedBooking;
    },
    updateModifiedProhibitedItemsDeclaration: (state, action: PayloadAction<boolean>) => {
      state.modifiedBooking.prohibitedItemsDeclared = action.payload;
    },
    updateModifiedPassengerField: (
      state,
      action: PayloadAction<{
        index: number;
        field: keyof PassengerInfo;
        value: PassengerFieldValue;
        subField?: string;
      }>,
    ) => {
      const { index, field, value, subField } = action.payload;
      if (index >= 0 && index < state.modifiedBooking.passengers.length) {
        const passenger = state.modifiedBooking.passengers[index];
        if (passenger) {
          if (subField && typeof passenger[field] === "object" && passenger[field] !== null) {
            (passenger[field] as Record<string, PassengerFieldValue>)[subField] = value;
          } else {
            (passenger as Record<string, PassengerFieldValue>)[field] = value;
          }
        }
      }
    },
    updateModifiedPassengerAdditionalInfo: (
      state,
      action: PayloadAction<{
        index: number;
        field: string;
        value: PassengerFieldValue;
      }>,
    ) => {
      const { index, field, value } = action.payload;
      if (index >= 0 && index < state.modifiedBooking.passengers.length) {
        const passenger = state.modifiedBooking.passengers[index];
        if (passenger) {
          passenger.additionalInformation ??= {};
          (passenger.additionalInformation as Record<string, PassengerFieldValue>)[field] = value;
        }
      }
    },
    updateModifiedPassengerInfant: (
      state,
      action: PayloadAction<{
        parentIndex: number;
        field: string;
        value: PassengerFieldValue;
      }>,
    ) => {
      const { parentIndex, field, value } = action.payload;
      if (parentIndex >= 0 && parentIndex < state.modifiedBooking.passengers.length) {
        const passenger = state.modifiedBooking.passengers[parentIndex];
        if (passenger?.infant) {
          // Handle nested field updates (e.g., "dateOfBirth.day", "documentExpirationDate.month")
          const fieldParts = field.split(".").filter(Boolean);
          if (fieldParts.length > 1) {
            let current: Record<string, PassengerFieldValue> = passenger.infant;

            // Navigate to the nested object
            for (let i = 0; i < fieldParts.length - 1; i++) {
              const key = fieldParts[i];
              if (key && (typeof current[key] !== "object" || current[key] === null)) {
                current[key] = {};
              }
              if (key) {
                current = current[key] as Record<string, PassengerFieldValue>;
              }
            }

            // Set the final value
            const lastKey = fieldParts.at(-1);
            if (lastKey) {
              current[lastKey] = value;
            }
          } else {
            (passenger.infant as Record<string, PassengerFieldValue>)[field] = value;
          }
        }
      }
    },
    assignModifiedSeat: (
      state,
      action: PayloadAction<{ segmentId: string; passengerId: number; seat: Seat | null }>,
    ) => {
      const { segmentId, passengerId, seat } = action.payload;

      const updateSegment = (segments: ModifiedBookingFlightSegment[]) => {
        const segment = segments.find((s) => s.id === segmentId);
        if (segment) {
          const passenger = segment.passengers.find((p) => p.id === passengerId);
          if (passenger) {
            passenger.seat = seat;
          }
        }
      };

      updateSegment(state.modifiedBooking.outbound);
      updateSegment(state.modifiedBooking.inbound);
    },

    setModifiedInboundSubtotal: (
      state,
      action: PayloadAction<{
        amount: number;
      }>,
    ) => {
      const { amount } = action.payload;
      state.modifiedBooking.inboundSubtotal = amount;
    },

    setModifiedOutboundSubtotal: (
      state,
      action: PayloadAction<{
        amount: number;
      }>,
    ) => {
      const { amount } = action.payload;
      state.modifiedBooking.outboundSubtotal = amount;
    },

    setOriginalBookingDetails: (state, action: PayloadAction<BookingDetails | null>) => {
      state.originalBookingDetails = action.payload;
    },

    setPreChangeCheckedInLegs: (state, action: PayloadAction<PreChangeCheckedInLegs | null>) => {
      state.preChangeCheckedInLegs = action.payload;
    },

    setBookingUserInfo: (state, action: PayloadAction<UserInfo | null>) => {
      state.bookingUserInfo = action.payload;
    },
  },
});

export const {
  setSelectedLeg,
  setOutboundDate,
  setReturnDate,
  setSelectedOutboundFlight,
  setSelectedReturnFlight,
  resetModifyBooking,

  // Modified booking actions (checkinSlice implementation)
  setModifiedBooking,
  resetModifiedBooking,
  updateModifiedProhibitedItemsDeclaration,
  updateModifiedPassengerField,
  updateModifiedPassengerAdditionalInfo,
  updateModifiedPassengerInfant,
  assignModifiedSeat,
  setModifiedInboundSubtotal,
  setModifiedOutboundSubtotal,
  setOriginalBookingDetails,
  setPreChangeCheckedInLegs,
  setBookingUserInfo,
  setSelectedOutboundFareDifference,
  setSelectedReturnFareDifference,
} = modifyBookingSlice.actions;

export default modifyBookingSlice.reducer;

// Hooks
export const useModifyBooking = () => useAppSelector((s) => s.modifyBooking);

export const useSelectedLeg = () => useAppSelector((s) => s.modifyBooking.selectedLeg);

export const useModifyDates = () =>
  useAppSelector((s) => ({
    outboundDate: s.modifyBooking.outboundDate,
    returnDate: s.modifyBooking.returnDate,
  }));

export const selectOutboundDate = (s: { modifyBooking: ModifyBookingState }) =>
  s.modifyBooking.outboundDate;

export const selectReturnDate = (s: { modifyBooking: ModifyBookingState }) =>
  s.modifyBooking.returnDate;

export const useSelectedFlights = () => useAppSelector((s) => s.modifyBooking.selectedFlights);
export const useSelectedFareDifference = () =>
  useAppSelector((s) => s.modifyBooking.selectedFareDifference);

// Modified booking selectors/hooks (checkinSlice implementation)
export const useModifiedBooking = () => useAppSelector((s) => s.modifyBooking.modifiedBooking);
export const useModifiedOutbound = () =>
  useAppSelector((s) => s.modifyBooking.modifiedBooking.outbound);
export const useModifiedInbound = () =>
  useAppSelector((s) => s.modifyBooking.modifiedBooking.inbound);
export const useModifiedPassengersCount = () =>
  useAppSelector((s) => s.modifyBooking.modifiedBooking.passengersCount);
export const useModifiedJourneyType = () =>
  useAppSelector((s) => s.modifyBooking.modifiedBooking.journeyType);
export const useModifiedPassengers = () =>
  useAppSelector((s) => s.modifyBooking.modifiedBooking.passengers);
export const useOriginalBookingDetails = () =>
  useAppSelector((s) => s.modifyBooking.originalBookingDetails);

export const usePreChangeCheckedInLegs = () =>
  useAppSelector((s) => s.modifyBooking.preChangeCheckedInLegs);

export const useBookingUserInfo = () => useAppSelector((s) => s.modifyBooking.bookingUserInfo);

// Hook to get grand total of extras (inbound + outbound subtotals)
export const useModifiedGrandTotal = () => {
  const outbound = useAppSelector(
    (state) => state.modifyBooking.modifiedBooking.outboundSubtotal ?? 0,
  );
  const inbound = useAppSelector(
    (state) => state.modifyBooking.modifiedBooking.inboundSubtotal ?? 0,
  );

  return outbound + inbound;
};

export const selectSelectedLeg = (s: { modifyBooking: ModifyBookingState }) =>
  s.modifyBooking.selectedLeg;

export const selectSelectedFlights = (s: { modifyBooking: ModifyBookingState }) =>
  s.modifyBooking.selectedFlights;

export const selectOriginalBookingDetails = (s: { modifyBooking: ModifyBookingState }) =>
  s.modifyBooking.originalBookingDetails;

export const selectIsInvoluntaryChange = (state: { modifyBooking: ModifyBookingState }) =>
  isInvoluntaryChangeFromQueues(state.modifyBooking.originalBookingDetails?.queues ?? []);

export const useIsInvoluntaryChange = () => useAppSelector(selectIsInvoluntaryChange);
